/* eslint-disable @typescript-eslint/no-explicit-any */
import moment from 'moment'
import React, { useEffect, useState, useRef, useCallback } from 'react'
import DatePicker from 'react-date-picker'
import 'react-date-picker/dist/DatePicker.css'
import { createUseStyles } from 'react-jss'
import Input from './Input'
import { Calendar as CalendarIcon, Close } from '../../../assets/icons'
import { baseButton } from '../../../theme/reuseables'
import { colors } from '../../../theme/palette'

type RuleNames =
    | 'datepickerWrapper'
    | 'datepicker'
    | 'button'
    | 'trigger'
    | 'close'

type Props = {
    label: string
    selected: Date | undefined | null
    setSelected: (date: Date | undefined | null) => void
    maxDate?: Date
    minDate?: Date
    disableCalendar?: boolean
    hasError?: boolean
    onChangeDateInputInvalid?: (isValid: boolean, errorMessage?: string) => void
}

interface StyleProps {
    hasValue?: boolean
    isDatePicker?: boolean
}

export const useStyles = createUseStyles<RuleNames, StyleProps, Jss.Theme>({
    datepickerWrapper: {
        border: 'none',
        position: 'relative',
        color: colors.black,
        '& label > input': {
            color: ({ hasValue }) => {
                return hasValue ? 'transparent' : 'black'
            },
        },
    },
    datepicker: {
        position: 'absolute',
        zIndex: 2,
        height: '100%',
        width: '100%',
        top: '0',
        right: '0',
        textAlign: 'right',
        border: `none`,
        borderRadius: '6px',
    },
    button: {
        ...baseButton,
        ...{
            cursor: 'pointer',
            padding: 0,
            margin: 0,
        },
    },
    trigger: {
        position: 'absolute',
        top: '16px',
        right: '10px',
        opacity: '.8',
    },
    close: {
        position: 'absolute',
        top: '18px',
        right: '28px',
    },
})

const DatePickerInput: React.FC<Props> = ({
    label,
    selected,
    setSelected,
    maxDate,
    minDate,
    disableCalendar,
    hasError,
    onChangeDateInputInvalid,
}) => {
    const datePickerRef = useRef<any>(null)
    const [dateValue, setdateValue] = useState(undefined)
    const [showPicker, setShowPicker] = useState(false)
    const [hasValue, setHasValue] = useState(false)
    const [inputValues, setInputValues] = useState({
        day: '',
        month: '',
        year: '',
    })

    // Function to validate the input values
    const validateInputs = useCallback(
        (values: {
            day: string
            month: string
            year: string
        }): { isValid: boolean; errorMessage: string } => {
            const { day, month, year } = values

            // Basic validation
            const dayNum = parseInt(day, 10)
            const monthNum = parseInt(month, 10)
            const yearNum = parseInt(year, 10)

            let isValid = true
            let errorMessage = ''

            if (day && (dayNum < 1 || dayNum > 31)) {
                isValid = false
                errorMessage = 'Datoen er ikke gyldig'
            }

            if (month && (monthNum < 1 || monthNum > 12)) {
                isValid = false
                errorMessage = 'Datoen er ikke gyldig'
            }

            if (
                year &&
                year.length === 4 &&
                (yearNum < 1900 || yearNum > 2100)
            ) {
                isValid = false
                errorMessage = 'Datoen er ikke indenfor gyldig tidsperiode'
            } else if (year && year.length !== 4) {
                isValid = false
                errorMessage = 'Datoen er ikke gyldig'
            }

            // Check if it's a valid date when all fields are filled
            if (day && month && year && year.length === 4 && isValid) {
                const dateStr = `${day.padStart(2, '0')}.${month.padStart(
                    2,
                    '0'
                )}.${year}`
                const testDate = moment(dateStr, 'DD.MM.YYYY', true)

                if (!testDate.isValid()) {
                    isValid = false
                    errorMessage = 'Datoen er ikke gyldig'
                }
            }

            // You can use this to show validation errors in your UI
            return { isValid, errorMessage }
        },
        []
    )

    // Function to watch and validate the input values
    const watchInputValues = useCallback((): void => {
        // The day is the first, the month is the second, the year is the third.
        // inside react-date-picker__inputGroup.

        if (datePickerRef.current) {
            // Find the input group first
            const inputGroup = datePickerRef.current.querySelector(
                '.react-date-picker__inputGroup'
            )

            if (inputGroup) {
                // Get all input elements inside the input group
                const allInputs = inputGroup.querySelectorAll('input')

                if (allInputs.length >= 3) {
                    // Day is the first input, month is the second, year is the third
                    const dayInput = allInputs[1]
                    const monthInput = allInputs[2]
                    const yearInput = allInputs[3]

                    const newValues = {
                        day: dayInput.value,
                        month: monthInput.value,
                        year: yearInput.value,
                    }

                    // Only validate if values have actually changed
                    const currentValues = `${newValues.day}-${newValues.month}-${newValues.year}`
                    const previousValues = `${inputValues.day}-${inputValues.month}-${inputValues.year}`

                    if (currentValues !== previousValues) {
                        setInputValues(newValues)

                        // Validate the inputs
                        const validationResult = validateInputs(newValues)
                        onChangeDateInputInvalid?.(
                            validationResult.isValid,
                            validationResult.errorMessage ??
                                'Datoen er ikke gyldig'
                        )
                    }
                }
            }
        }
    }, [validateInputs, onChangeDateInputInvalid, inputValues])

    const classes = useStyles({
        ...{ hasValue },
    })

    useEffect(() => {
        if (dateValue && dateValue.length !== 0) {
            setHasValue(true)
        } else {
            setHasValue(false)
        }
    }, [dateValue])

    useEffect(() => {
        if (!selected) {
            setdateValue(undefined)
        }
    }, [selected])

    // Set up watchers for the input elements
    useEffect(() => {
        const setupWatchers = (): (() => void) | undefined => {
            if (datePickerRef.current) {
                // Find the input group first
                const inputGroup = datePickerRef.current.querySelector(
                    '.react-date-picker__inputGroup'
                )

                if (inputGroup) {
                    // Get all input elements inside the input group
                    const allInputs = inputGroup.querySelectorAll('input')

                    if (allInputs.length >= 3) {
                        // Day is the first input, month is the second, year is the third
                        const dayInput = allInputs[1]
                        const monthInput = allInputs[2]
                        const yearInput = allInputs[3]

                        // Add event listeners to watch for changes
                        const handleInputEvent = (): void => {
                            // Use setTimeout to ensure the input value has been updated
                            setTimeout(() => {
                                watchInputValues()
                            }, 0)
                        }

                        // Also watch for programmatic changes using MutationObserver
                        const observer = new MutationObserver(() => {
                            setTimeout(() => {
                                watchInputValues()
                            }, 0)
                        })

                        // Observe changes to the input group
                        observer.observe(inputGroup, {
                            childList: true,
                            subtree: true,
                            attributes: true,
                            attributeFilter: ['value'],
                        })

                        dayInput.addEventListener('input', handleInputEvent)
                        monthInput.addEventListener('input', handleInputEvent)
                        yearInput.addEventListener('input', handleInputEvent)

                        // Initial check
                        watchInputValues()

                        // Return cleanup function
                        return () => {
                            observer.disconnect()
                            dayInput.removeEventListener(
                                'input',
                                handleInputEvent
                            )
                            monthInput.removeEventListener(
                                'input',
                                handleInputEvent
                            )
                            yearInput.removeEventListener(
                                'input',
                                handleInputEvent
                            )
                        }
                    }
                    return undefined
                }
                return undefined
            }
            return undefined
        }

        // Use a longer delay and retry mechanism to ensure the DatePicker has rendered
        const timer = setTimeout(() => {
            const cleanup = setupWatchers()
            if (!cleanup) {
                // If setup failed, retry after a longer delay
                const retryTimer = setTimeout(setupWatchers, 500)
                return () => clearTimeout(retryTimer)
            }
            return undefined
        }, 200)

        return () => {
            clearTimeout(timer)
        }
    }, [watchInputValues])

    const handleOnChangeDate = (val: any): void => {
        if (val && typeof val === 'object') {
            const asText = moment(val).format('DD.MM.YYYY')
            setdateValue(asText)
        } else {
            // Handle null/undefined case (clear button)
            setdateValue(undefined)
        }
        setShowPicker(false)
        setSelected(val)
    }

    return (
        <div className={classes.datepickerWrapper}>
            <Input
                label={label}
                required={false}
                disabled={showPicker}
                isDatePicker
            />
            <div
                className={classes.datepicker}
                style={{
                    cursor: 'pointer',
                    border: `1px solid ${
                        hasError ? colors.error : colors.borderColor
                    }`,
                    borderRadius: '5px',
                }}
                ref={datePickerRef}
            >
                <DatePicker
                    disableCalendar={disableCalendar}
                    onChange={handleOnChangeDate}
                    value={selected}
                    maxDate={maxDate}
                    minDate={minDate}
                    calendarIcon={
                        <CalendarIcon width="16px" color={colors.green500} />
                    }
                    format="dd.MM.y"
                    dayPlaceholder="dd"
                    monthPlaceholder="mm"
                    yearPlaceholder="åååå"
                    closeCalendar
                    clearIcon={<Close width="16px" color={colors.green500} />}
                />
            </div>
        </div>
    )
}

export { DatePickerInput }
